import { authenticate, logout as logoutApi, me, refreshToken as refreshTokenApi, type TokenResponse } from './api';
import { setUnauthorizedHandler } from './authFetch';
import { userStore } from './store';

export const REFRESH_TOKEN_STORAGE_KEY = 'pay-roadrunner.refreshToken';
const REFRESH_LEEWAY_MS = 60_000;

export interface AuthApiClient {
  authenticate: typeof authenticate;
  refreshToken: typeof refreshTokenApi;
  me: typeof me;
  logout?: typeof logoutApi;
}

interface AuthServiceOptions {
  api?: AuthApiClient;
  storage?: Storage;
  redirectToLogin?: () => void;
}

export class AuthService {
  private api: AuthApiClient;
  private storage: Storage;
  private redirectToLogin: () => void;
  private refreshTimer: ReturnType<typeof window.setTimeout> | null = null;
  private initPromise: Promise<boolean> | null = null;
  private currentRefreshToken: string | null = null;

  constructor({ api = { authenticate, refreshToken: refreshTokenApi, me, logout: logoutApi }, storage = window.localStorage, redirectToLogin = () => window.location.assign('/login') }: AuthServiceOptions = {}) {
    this.api = api;
    this.storage = storage;
    this.redirectToLogin = redirectToLogin;
    window.addEventListener('focus', this.handleFocus);
  }

  async login(email: string, password: string): Promise<void> {
    const response = await this.api.authenticate(email, password);
    userStore.startNewSession();
    await this.applyTokenResponse(response);
  }

  logout({ redirect = true }: { redirect?: boolean } = {}): void {
    // PRR-421 D-007: fire-and-forget, best-effort — a failed or slow logout call must never
    // block clearing local state or the redirect below. Only called when a session actually
    // exists; there is nothing to record for an already-logged-out client.
    if (userStore.token) void this.api.logout?.().catch(() => undefined);
    this.stopRefreshTimer();
    userStore.clearAuth();
    this.currentRefreshToken = null;
    userStore.setInitialising(false);
    this.storage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    if (redirect) this.redirectToLogin();
  }

  initFromStorage(): Promise<boolean> {
    this.initPromise ??= this.runInitFromStorage().finally(() => {
      this.initPromise = null;
    });
    return this.initPromise;
  }

  async refreshToken(): Promise<void> {
    const storedRefreshToken = this.storage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!storedRefreshToken) {
      this.logout({ redirect: true });
      return;
    }

    // Another tab may replace the shared refresh token with a different account's.
    // A normal rotation is recorded by applyTokenResponse and keeps this session.
    if (this.currentRefreshToken && storedRefreshToken !== this.currentRefreshToken)
      userStore.startNewSession();

    try {
      const response = await this.api.refreshToken(storedRefreshToken);
      await this.applyTokenResponse(response);
    } catch (error) {
      this.logout({ redirect: true });
      throw error;
    }
  }

  dispose(): void {
    this.stopRefreshTimer();
    window.removeEventListener('focus', this.handleFocus);
  }

  private async runInitFromStorage(): Promise<boolean> {
    userStore.setInitialising(true);
    const storedRefreshToken = this.storage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!storedRefreshToken) {
      userStore.clearAuth();
      userStore.setInitialising(false);
      return false;
    }

    try {
      const response = await this.api.refreshToken(storedRefreshToken);
      await this.applyTokenResponse(response);
      return true;
    } catch {
      this.logout({ redirect: false });
      return false;
    } finally {
      userStore.setInitialising(false);
    }
  }

  private async applyTokenResponse(response: TokenResponse): Promise<void> {
    userStore.setAuth({ token: response.token, expiresAt: response.expiresAt });
    userStore.setInitialising(false);
    this.storage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.refreshToken);
    this.currentRefreshToken = response.refreshToken;
    this.startRefreshTimer();
    await this.loadRoles();
  }

  /**
   * PRR-316 D-008: nav visibility (Administration -> Users & Access) reads roles from the
   * server's canonical source rather than a separate hardcoded frontend role model.
   * Client-side visibility is UX only — every user-management endpoint independently
   * enforces ADMIN server-side, so a failure here degrades to "item hidden", not a security
   * gap.
   */
  private async loadRoles(): Promise<void> {
    try {
      const info = await this.api.me();
      userStore.setRoles(info.roles ?? []);
      userStore.setTenantCompanyName(info.tenantCompanyName ?? '');
    } catch {
      userStore.setRoles([]);
      userStore.setTenantCompanyName('');
    }
  }

  private startRefreshTimer(): void {
    this.stopRefreshTimer();
    if (!userStore.expiresAt) return;

    const delay = Math.max(userStore.expiresAt * 1000 - Date.now() - REFRESH_LEEWAY_MS, 0);
    this.refreshTimer = window.setTimeout(() => {
      void this.refreshToken();
    }, delay);
  }

  private stopRefreshTimer(): void {
    if (!this.refreshTimer) return;
    window.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private handleFocus = (): void => {
    if (userStore.isAuthenticated) {
      this.startRefreshTimer();
    }
  };
}

export const authService = new AuthService();
setUnauthorizedHandler(() => authService.refreshToken());
